import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { cgGet, cgFindId } from '../services/coingecko';

// localStorage 缓存：项目 symbol → CoinGecko logo URL。避免老 watchlist 行每次开页都重拉 CG。
// 失败的查询也缓存（存 null），减少无效重试。
const LOGO_CACHE_KEY = 'pl.watchlist.logo.cache.v1';
const LOGO_CACHE_TTL_MS = 30 * 24 * 3600 * 1000; // 30 天
function loadLogoCache() {
  try {
    const raw = localStorage.getItem(LOGO_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v && now - (v.t || 0) < LOGO_CACHE_TTL_MS)
    );
  } catch { return {}; }
}
function saveLogoCache(cache) {
  try { localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify(cache)); } catch {}
}

async function fetchCgLogo(tokenSymbol, projectName) {
  if (!tokenSymbol && !projectName) return null;
  try {
    const id = await cgFindId(tokenSymbol || '', projectName || '').catch(() => null);
    if (!id) return null;
    const coin = await cgGet(`/coins/${id}?localization=false&tickers=false&market_data=false&community_data=false&developer_data=false&sparkline=false`).catch(() => null);
    return coin?.image?.small || coin?.image?.thumb || null;
  } catch { return null; }
}

export function useWatchlist(user) {
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWatchlist = useCallback(async () => {
    if (!user || !supabase) { setWatchlist([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from('watchlists')
      .select('*')
      .order('created_at', { ascending: false });
    setWatchlist(data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  // 老条目 logo backfill：watchlist 加载后，找出 details.logo 缺失的行，
  // 通过 CoinGecko lazy 拉一次填上。localStorage 缓存 30 天，下次开页直接命中。
  // 仅前端 patch，不写库 — 后续 fresh re-analyze 自然会用 RootData logo 覆盖。
  useEffect(() => {
    if (!watchlist || watchlist.length === 0) return;
    const cache = loadLogoCache();
    const missing = watchlist.filter(w => {
      const p = w.project_data || {};
      return !p.details?.logo;
    });
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const patches = {};
      for (const w of missing) {
        const p = w.project_data || {};
        const sym = (p.details?.token_symbol || '').toUpperCase().trim();
        const name = w.project_name || p.name || '';
        const cacheKey = sym || name.toLowerCase();
        if (!cacheKey) continue;
        // 命中本地缓存（包含 null = 之前查过没结果）
        if (cacheKey in cache) {
          if (cache[cacheKey].logo) patches[w.id] = cache[cacheKey].logo;
          continue;
        }
        const logo = await fetchCgLogo(sym, name);
        cache[cacheKey] = { logo, t: Date.now() };
        if (logo) patches[w.id] = logo;
      }
      saveLogoCache(cache);
      if (cancelled || Object.keys(patches).length === 0) return;
      setWatchlist(prev => prev.map(w => {
        if (!patches[w.id]) return w;
        const p = w.project_data || {};
        return {
          ...w,
          project_data: {
            ...p,
            details: { ...(p.details || {}), logo: patches[w.id] },
          },
        };
      }));
    })();

    return () => { cancelled = true; };
  }, [watchlist.length]); // 仅在数量变化时再跑（fetchWatchlist 后）

  const addToWatchlist = async (project) => {
    if (!user) return { error: new Error('请先登录') };
    if (!supabase) return { error: new Error('Supabase not configured') };
    const projectData = {
      project_name: project.name || project.details?.project_name,
      project_data: {
        id: project.id,
        name: project.name,
        analysis: project.analysis,
        details: project.details ? {
          one_liner: project.details.one_liner,
          // RootData 返回字段是 logo；老缓存可能用 logo_url，存两边兼容
          logo: project.details.logo || project.details.logo_url || null,
          logo_url: project.details.logo_url || project.details.logo || null,
          token_symbol: project.details.token_symbol,
          total_funding: project.details.total_funding,
        } : null,
        cgData: project.cgData ? {
          market_data: {
            current_price: project.cgData.market_data?.current_price,
            fully_diluted_valuation: project.cgData.market_data?.fully_diluted_valuation,
          },
        } : null,
      },
    };
    const { error } = await supabase.from('watchlists').insert({
      user_id: user.id,
      ...projectData,
    });
    if (!error) {
      fetchWatchlist();
      // Fire-and-forget: 触发后端 firstDoc 探测，下次打开 Watchlist 即可命中缓存
      // 仅当项目还没有 firstDoc 数据时才探测（避免重复 Wayback 查询）
      try {
        const hasFirstDoc = (project?.timeline || []).some(t => t.key === 'firstDoc');
        if (!hasFirstDoc) {
          fetch('/api/firstdoc-batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projects: [{
                project_name: projectData.project_name,
                project_id: project.id || project.details?.project_id,
              }],
            }),
          }).catch(() => {}); // 静默失败，不影响 add 流程
        }
      } catch {}
    }
    return { error };
  };

  const removeFromWatchlist = async (projectName) => {
    if (!supabase) return { error: new Error('Supabase not configured') };
    // 大小写不敏感：watchlist 存的是规范名，用户可能从不同入口（搜索/portfolio/详情）传入不同大小写
    const safeName = String(projectName || '').replace(/[%_\\]/g, '\\$&');
    const { error } = await supabase
      .from('watchlists')
      .delete()
      .eq('user_id', user.id)
      .ilike('project_name', safeName);
    if (!error) fetchWatchlist();
    return { error };
  };

  const isInWatchlist = (projectName) => {
    const target = (projectName || '').toLowerCase();
    return watchlist.some(w => (w.project_name || '').toLowerCase() === target);
  };

  return { watchlist, loading, addToWatchlist, removeFromWatchlist, isInWatchlist, refetch: fetchWatchlist };
}
